import { z } from "zod";
import { MAX_REPLY_PRODUCT_CARDS, transferReasonSchema } from "./reply";
import { orderCardSchema } from "./order-card";
import { cardBlockSchema } from "./storefront-cards";
import { supportRequestPayloadSchema } from "./support-request";

import {
  isoDateTimeSchema,
  moneySchema,
  pageQuerySchema,
  pageSchema,
} from "./common";

/**
 * 事实的来源标注，展示成 `Size chart · Alpine Shell` 这种一行字。
 * label 由 AI 后端生成，前端不从 source_types 推断业务文案。
 */
export const sourceSchema = z.object({
  label: z.string().min(1),
  url: z.url().nullable(),
});

/**
 * 价格后端可能给不出（见 reply.ts），给不出就不展示，不牵连整条会话。
 * `id` 是变体行的 item_id；`parentItemId` 指回父商品，`variantId` 是 Shopify 的变体 id，
 * 后端给不出就是 null。回放要和店面看到的是同一张卡，id 不在这里截断。
 */
export const productCardSchema = z.object({
  id: z.string().min(1),
  parentItemId: z.string().nullable(),
  variantId: z.string().nullable(),
  title: z.string().min(1),
  price: moneySchema.nullable(),
  imageUrl: z.url().nullable(),
  url: z.url(),
});

export type ProductCard = z.infer<typeof productCardSchema>;

/** 只读结果字段白名单；未知字段（包括动作、凭据和内部引用）不进入商家浏览器。 */
const [orderList, orderDetail] = orderCardSchema.options;
const orderFactsSchema = orderList.shape.payload.shape.orders.element
  .omit({
    order_ref: true,
  })
  .strip();
export const inboxOrderListDataSchema = orderList.shape.payload
  .omit({ next_cursor: true, limit_reached: true })
  .strip()
  .extend({ orders: z.array(orderFactsSchema).max(50) });
export const inboxOrderDetailDataSchema = orderDetail.shape.payload
  .omit({
    order_ref: true,
    order_status_url: true,
    actions: true,
  })
  .strip();
const sentEmailPayloadSchema = z
  .object(supportRequestPayloadSchema.shape)
  .pick({
    request_id: true,
    request_type: true,
    accepted_at: true,
    send_status: true,
    contact_email: true,
    to: true,
  })
  .strip()
  .extend({
    request_type: supportRequestPayloadSchema.shape.request_type.optional(),
    send_status: supportRequestPayloadSchema.shape.send_status
      .unwrap()
      .exclude(["draft"]),
  });
export const emailPayloadSchema = z.union([
  sentEmailPayloadSchema,
  z
    .object({
      request_id: supportRequestPayloadSchema.shape.request_id,
      request_type: supportRequestPayloadSchema.shape.request_type,
      phase: z.enum(["draft", "paused"]),
      send_status: z.literal("draft"),
      contact_email: supportRequestPayloadSchema.shape.contact_email,
      to: supportRequestPayloadSchema.shape.to,
    })
    .strip(),
  z
    .object({
      request_id: supportRequestPayloadSchema.shape.request_id,
      phase: z.literal("expired"),
    })
    .strip(),
]);
/** 店面块的只读子集；保留相同信封，数据再次经过商家白名单。 */
export const inboxBlockSchema = z.union([
  cardBlockSchema.options[0].extend({
    type: z.enum(["order_list", "order_list_empty", "order_detail"]),
  }),
  cardBlockSchema.options[3].extend({
    actions: cardBlockSchema.options[0].shape.actions,
    data: inboxOrderListDataSchema,
  }),
  cardBlockSchema.options[4].extend({
    actions: cardBlockSchema.options[0].shape.actions,
  }),
  cardBlockSchema.options[5].extend({
    actions: cardBlockSchema.options[0].shape.actions,
    data: inboxOrderDetailDataSchema,
  }),
  cardBlockSchema.options[7].extend({
    actions: cardBlockSchema.options[0].shape.actions,
    data: emailPayloadSchema,
  }),
]);
export type InboxBlock = z.infer<typeof inboxBlockSchema>;

/**
 * 买家对一条 AI 回复的一次性反馈快照；reasonName 按店铺当前归因配置解析，
 * 配置里没这个代码时为 null。
 */
export const messageFeedbackSchema = z.object({
  rating: z.enum(["positive", "negative"]),
  reasonCode: z.string().min(1).nullable(),
  reasonName: z.string().min(1).nullable(),
  details: z.string().min(1).nullable(),
  memberName: z.string().min(1).nullable(),
  createdAt: isoDateTimeSchema,
});

export type MessageFeedback = z.infer<typeof messageFeedbackSchema>;

const messageBaseSchema = z.object({
  id: z.string().min(1),
  sentAt: isoDateTimeSchema,
  /** 原始正文，保留 JSON 消息块，由两端共享的 readReplyContent 解析。 */
  text: z.string(),
});

const buyerMessageSchema = messageBaseSchema.extend({
  role: z.literal("buyer"),
});

/**
 * 一条 AI 回复：正文、商品与本地记录生成的只读块，保留原始消息锚点。
 * 来源、商品卡和只读块可以同时出现。
 * 后续加尺码卡、对比卡时在这里加一个可选挂件，店面和后台一起拿到。
 */
const assistantMessageSchema = messageBaseSchema.extend({
  role: z.literal("assistant"),
  sources: z.array(sourceSchema),
  products: z.array(productCardSchema).max(MAX_REPLY_PRODUCT_CARDS),
  blocks: z.array(inboxBlockSchema),
  transferReason: transferReasonSchema.nullable(),
  feedback: messageFeedbackSchema.nullable(),
});

export const conversationMessageSchema = z.discriminatedUnion("role", [
  buyerMessageSchema,
  assistantMessageSchema,
]);

export type ConversationMessage = z.infer<typeof conversationMessageSchema>;

export type AssistantMessage = z.infer<typeof assistantMessageSchema>;

/**
 * Inbox 一行一个原始会话。`visitorLabel` 是 ztalkai 给出的访客编号；
 * `handoff` 表示会话内至少一条 AI 回复的 transfer_reason 非空。
 * `lastMessagePreview` 是最后一条可见消息的纯文本预览，给不出时为 null。
 * Playground 会话不会出现在这里。
 */
export const conversationSummarySchema = z.object({
  id: z.string().min(1),
  visitorLabel: z.string().min(1),
  lastMessagePreview: z.string().min(1).nullable(),
  startedAt: isoDateTimeSchema,
  lastMessageAt: isoDateTimeSchema,
  messageCount: z.int().nonnegative(),
  handoff: z.boolean(),
  handoffRequestSent: z.boolean().optional(),
});

export type ConversationSummary = z.infer<typeof conversationSummarySchema>;

/** 游标定位会话列表；不再按顾客归并或查询成员补页。 */
export const inboxConversationsQuerySchema = pageQuerySchema;

/** `GET /api/inbox/conversations`，每行一个会话，按最近活跃倒序。 */
export const inboxConversationsResponseSchema = pageSchema(
  conversationSummarySchema,
);

export type InboxConversationsResponse = z.infer<
  typeof inboxConversationsResponseSchema
>;

/** `GET /api/inbox/conversations/{id}/messages`，整段会话按原始顺序一次返回。 */
export const conversationMessagesResponseSchema = z.object({
  conversation: conversationSummarySchema,
  messages: z.array(conversationMessageSchema),
});

export type ConversationMessagesResponse = z.infer<
  typeof conversationMessagesResponseSchema
>;
